import Toast from "bootstrap/js/dist/toast";
import { memo, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { userSelector } from "store/auth";
import { actions as flashesActions } from "store/flashes";
import { isObject } from "utils/helpers";
import vars from "utils/vars";
import LocaleLink from "views/components/LocaleLink";
import { ABILITIES, can } from "utils/helpers/abilities";

const ToastsListItem = ({
	message: { message, id, validationErrors, actionLabel, actionUrl, actionTarget },
	type,
	typeIndex,
	messageIndex,
	isUiVersionUpdate,
	disableAutoHide,
	reloadPage,
}) => {
	const dispatch = useDispatch();
	const { t } = useTranslation();

	// redux global state
	const user = useSelector(userSelector);

	// ref hook
	const toastRef = useRef(null);

	// constants
	const isUserOwner = can(user, ABILITIES.manageBilling);

	// effect hooks
	useEffect(() => {
		const toastRefCurrent = toastRef?.current;
		let toast = Toast.getInstance(toastRefCurrent);
		const options = {
			...(disableAutoHide ? { autohide: false } : { delay: vars.app.toastsHideDelayMs }),
		};

		if (!toast) {
			toast = new Toast(toastRefCurrent, options)?.show();
			if (!isUiVersionUpdate) {
				toastRefCurrent.addEventListener("hidden.bs.toast", () =>
					dispatch(flashesActions.remove({ type, message: { id } }))
				);
			}
		}

		return () => {
			if (toast) Toast.getInstance(toastRefCurrent)?.hide();
		};
	}, [dispatch, id, type, isUiVersionUpdate, disableAutoHide]);

	return (
		<div
			ref={toastRef}
			className={`toast fade align-items-center ${
				type?.toLowerCase() !== "info" ? "text-white" : ""
			} w-100 bg-${type}`}
			id={isUiVersionUpdate ? "toastUiVersionUpdate" : `toast-${typeIndex}-${messageIndex}`}
			role="alert"
			aria-live="assertive"
			aria-atomic="true">
			<div className="toast-body white-space-pre-line">
				<div className="row flex-nowrap w-100">
					<div className="col">
						{message?.toLowerCase() === "no found profile" ? (
							<p className="mb-0 text-break">
								<span>No found profile. Please</span>
								<button
									type="button"
									className="btn btn-link btn-sm ms-1 text-decoration-underline text-reset d-inline-block"
									data-bs-toggle="modal"
									data-bs-target="#patientFormModal">
									<strong>Create a new Patient profile</strong>
								</button>
							</p>
						) : message?.toLowerCase() === "order has limit" ? (
							<p className="mb-0 text-break">
								{isUserOwner ? (
									<>
										<span>
											This account has reached the Orders quota limit, and this item is currently
											locked. To access it, please
										</span>{" "}
										<LocaleLink
											to="/subscriptions/extra"
											className="text-reset lh-1 text-decoration-underline">
											<strong>Purchase an order bundle</strong>
										</LocaleLink>
									</>
								) : (
									<span>
										This account has reached the Orders quota limit, and this item is currently
										locked. To access it, the account holder shall purchase an orders bundle.
									</span>
								)}
							</p>
						) : message?.toLowerCase() === "expired" ? (
							<p className="mb-0 text-break">
								{t("system_messages.expired_message_1")}
								<LocaleLink to="/auth/login" className="mx-1 text-reset text-decoration-underline">
									<strong>{t("actions.login_page")}</strong>
								</LocaleLink>
								{t("system_messages.expired_message_2")}
							</p>
						) : (
							<p className="mb-0 text-break">
								<span className="hstack gap-2">
									<span>{message}</span>
									{actionLabel && actionUrl && (
										<a
											href={actionUrl}
											target={actionTarget || "_blank"}
											rel="noreferrer"
											className="btn btn-light btn-sm px-2 py-1 ms-auto text-uppercase">
											<small>
												<strong>{actionLabel}</strong>
											</small>
										</a>
									)}
									{isUiVersionUpdate && (
										<button
											className="btn btn-primary btn-sm px-3 py-1 text-uppercase ms-auto white-space-nowrap"
											type="button"
											onClick={reloadPage}>
											<small>
												<strong>refresh</strong>
											</small>
										</button>
									)}
								</span>
								{isObject(validationErrors) && (
									<ul className="m-0 p-0 d-block">
										{Object.entries(validationErrors).map(([key, value]) => (
											<li key={key} className="hstack gap-1 flex nowrap">
												<strong className="flex-shrink-0">{`${key}:`}</strong>
												<span className="flex-grow-1">{`${value}.`}</span>
											</li>
										))}
									</ul>
								)}
							</p>
						)}
					</div>
					{!isUiVersionUpdate && (
						<div className="col-auto">
							<button
								type="button"
								className={`btn-close ${
									type?.toLowerCase() !== "info" ? "btn-close-white" : ""
								} ms-auto fs-6`}
								data-bs-dismiss="toast"
								aria-label={t("actions.close")}></button>
						</div>
					)}
				</div>
			</div>
		</div>
	);
};

export default memo(ToastsListItem);
